import { useEffect } from 'react';
import { Loader2, RefreshCw, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useGenerationJob, useRetryJob } from '@/hooks/useGenerationJobs';
import StatusBadge from './StatusBadge';

interface JobStatusViewProps {
  jobId: string;
  onBackToList: () => void;
}

export default function JobStatusView({ jobId, onBackToList }: JobStatusViewProps) {
  const { data, isLoading, refetch, isFetching, dataUpdatedAt } = useGenerationJob(jobId);
  const retryMutation = useRetryJob();

  const job = data?.data;

  // Poll every 30 seconds when IN_PROGRESS
  useEffect(() => {
    if (job?.status !== 'IN_PROGRESS') return;
    const interval = setInterval(() => refetch(), 30 * 1000);
    return () => clearInterval(interval);
  }, [job?.status, refetch]);

  const lastChecked = dataUpdatedAt
    ? new Date(dataUpdatedAt).toLocaleTimeString()
    : null;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-48 bg-admin-dark-700" />
        <Skeleton className="h-32 w-full bg-admin-dark-700" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-admin-slate-500 text-center py-12">
        Job not found.
        <div className="mt-4">
          <Button
            variant="secondary"
            onClick={onBackToList}
            className="bg-admin-dark-700 hover:bg-admin-dark-600 text-admin-slate-400"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Activity Generation
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-admin-dark-800 border-admin-slate-300/10">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-heading-4 text-admin-slate-400">
              Generation Job
            </CardTitle>
            <StatusBadge status={job.status} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Job details */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-admin-slate-600 text-xs uppercase tracking-wide mb-1">
                Job ID
              </p>
              <p className="text-admin-slate-400 font-mono">{job.id}</p>
            </div>
            <div>
              <p className="text-admin-slate-600 text-xs uppercase tracking-wide mb-1">
                Time Period
              </p>
              <p className="text-admin-slate-400">{job.transition}</p>
            </div>
            <div>
              <p className="text-admin-slate-600 text-xs uppercase tracking-wide mb-1">
                Age Group
              </p>
              <p className="text-admin-slate-400">{job.ageGroup} years</p>
            </div>
            <div>
              <p className="text-admin-slate-600 text-xs uppercase tracking-wide mb-1">
                Requested
              </p>
              <p className="text-admin-slate-400">{job.requestedCount} activities</p>
            </div>
          </div>

          {/* Status-specific content */}
          {job.status === 'IN_PROGRESS' && (
            <div className="flex items-center gap-3 p-3 rounded-md bg-amber-500/10 border border-amber-500/20">
              <Loader2 className="w-4 h-4 text-amber-500 animate-spin flex-shrink-0" />
              <p className="text-sm text-amber-600">
                Generation is in progress. This may take a few minutes.
              </p>
            </div>
          )}

          {job.status === 'COMPLETED' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 rounded-md bg-emerald-500/5 border border-emerald-500/10">
              <div className="text-center">
                <p className="text-2xl font-semibold text-emerald-500">
                  {job.generatedCount ?? 0}
                </p>
                <p className="text-xs text-admin-slate-500 mt-1">Generated</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-semibold text-emerald-600">
                  {job.validCount ?? 0}
                </p>
                <p className="text-xs text-admin-slate-500 mt-1">Valid</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-semibold text-red-400">
                  {job.invalidCount ?? 0}
                </p>
                <p className="text-xs text-admin-slate-500 mt-1">Invalid</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-semibold text-admin-slate-400">
                  {job.savedActivityIds?.length ?? 0}
                </p>
                <p className="text-xs text-admin-slate-500 mt-1">Saved</p>
              </div>
            </div>
          )}

          {job.status === 'FAILED' && (
            <div className="p-4 rounded-md bg-red-500/10 border border-red-500/20 space-y-3">
              <p className="text-sm text-red-400">
                {job.errorMessage || 'An unknown error occurred during generation.'}
              </p>
              <Button
                onClick={() => retryMutation.mutate(job.id)}
                disabled={retryMutation.isPending}
                className="bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/20"
                size="sm"
              >
                {retryMutation.isPending ? (
                  <>
                    <Loader2 className="w-3 h-3 mr-2 animate-spin" />
                    Retrying...
                  </>
                ) : (
                  'Retry Job'
                )}
              </Button>
            </div>
          )}

          {/* Last checked / refresh */}
          <div className="flex items-center justify-between text-sm text-admin-slate-500 pt-2 border-t border-admin-slate-300/10">
            <span>
              {lastChecked ? `Last checked: ${lastChecked}` : 'Loading...'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => refetch()}
              disabled={isFetching}
              className="text-admin-slate-500 hover:text-admin-slate-400"
            >
              {isFetching ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              <span className="ml-2">Check Status Now</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      <Button
        variant="secondary"
        onClick={onBackToList}
        className="bg-admin-dark-700 hover:bg-admin-dark-600 text-admin-slate-400"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Activity Generation
      </Button>
    </div>
  );
}
